import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { PatchAdminV1OrgProductsIdBody } from '@nucleus-commerce/api-client/admin';

import { productStockQueryOptions } from '@/features/inventory';

import { productsApi } from '../api/products-api';

import { productKeys } from './product-keys';

/**
 * Errors are rendered by the form. The save can change the variant set, so the fresh list goes into
 * the cache together with the product and the editor never renders one against a stale copy of the
 * other; stock is per variant, so its query is refreshed too.
 */
export function useUpdateProduct(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PatchAdminV1OrgProductsIdBody) => productsApi.update(productId, input),
    meta: { successMessage: 'products:toasts.updated', silentError: true },
    onSuccess: async (product) => {
      // Through the query, not a separate read: one Save can run this next to the variant card's
      // save, and an invalidation restarts a read already under way, so the last one sees both.
      await queryClient.invalidateQueries({
        queryKey: productKeys.variants(productId),
        refetchType: 'all',
      });
      queryClient.setQueryData(productKeys.detail(productId), product);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: productKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: productKeys.vendors() }),
        queryClient.invalidateQueries({ queryKey: productStockQueryOptions(productId).queryKey }),
      ]);
    },
  });
}
